import { useEffect, useState } from "react";
import { categories } from "@/mocks/techData";

export default function CategoryShowcase() {
  const [active, setActive] = useState(0);
  const current = categories[active];

  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      const idx = categories.findIndex((c) => c.id === id);
      if (idx >= 0) setActive(idx);
    };
    window.addEventListener("fist:select-category", handler);
    return () => window.removeEventListener("fist:select-category", handler);
  }, []);

  const change = (i: number) => setActive(i);

  return (
    <section id="categories" className="bg-background-50 py-20 md:py-28">
      <div className="container-x">
        <div className="mb-10 max-w-2xl md:mb-14">
          <p className="eyebrow mb-4 flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-foreground-950"></span>
            Shop by Category
          </p>
          <h2 className="section-title">
            Every essential, <span className="text-primary-600">in one place.</span>
          </h2>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr] lg:items-stretch">
          {/* Main visual */}
          <div className="relative order-2 min-h-[280px] overflow-hidden rounded-xl bg-foreground-950 sm:min-h-[380px] lg:order-1">
            <img
              key={current.id}
              src={current.image}
              alt={current.blurb}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ animation: "catIn .7s ease-out" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent"></div>
            <div
              key={`cap-${current.id}`}
              className="absolute bottom-0 left-0 p-5 md:p-8"
              style={{ animation: "catUp .6s .08s ease-out both" }}
            >
              <span className="rounded-full bg-primary-500 px-3 py-1 font-label text-[10px] text-foreground-950">
                {current.tag}
              </span>
              <h3 className="mt-3 font-heading text-2xl text-background-50 md:text-4xl">{current.label}</h3>
              <p className="mt-2 max-w-sm text-sm text-background-100/75">{current.blurb}</p>
            </div>
            <style>{`@keyframes catIn{from{opacity:0;transform:scale(1.05)}to{opacity:1;transform:scale(1)}}`}</style>
          </div>

          {/* Selector list */}
          <div className="order-1 flex flex-col lg:order-2 min-w-0">
            {/* Mobile: horizontal scrollable pills */}
            <div className="flex w-full max-w-full gap-2 overflow-x-auto pb-3 scrollbar-hide lg:hidden">
              {categories.map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => change(i)}
                  style={{ animation: active === i ? "pillPop .3s ease-out" : undefined }}
                  className={`shrink-0 cursor-pointer rounded-full border px-4 py-2.5 text-left font-label text-xs transition-all ${
                    active === i
                      ? "border-primary-500 bg-primary-500/10 text-primary-600"
                      : "border-foreground-200 text-foreground-500 hover:border-foreground-400"
                  }`}
                >
                  <span className={`mr-2 font-heading text-xs ${
                    active === i ? "text-primary-600" : "text-foreground-300"
                  }`}>
                    0{i + 1}
                  </span>
                  {c.label}
                </button>
              ))}
            </div>

            {/* Desktop: vertical list */}
            <div className="hidden flex-col border-t border-foreground-200 lg:flex lg:border-t-0">
              {categories.map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => change(i)}
                  style={{ animation: active === i ? "pillPop .3s ease-out" : undefined }}
                  className={`group flex cursor-pointer items-center gap-4 border-b border-foreground-200 py-5 text-left transition-colors ${
                    active === i ? "" : "hover:bg-foreground-50"
                  }`}
                >
                  <span
                    className={`font-heading text-xs ${
                      active === i ? "text-primary-600" : "text-foreground-300"
                    }`}
                  >
                    0{i + 1}
                  </span>
                  <span
                    className={`flex-1 font-heading text-xl transition-colors ${
                      active === i ? "text-foreground-950" : "text-foreground-500"
                    }`}
                  >
                    {c.label}
                  </span>
                  <span className="text-xs text-foreground-400">{c.tag}</span>
                  <i
                    className={`ri-arrow-right-line transition-transform ${
                      active === i ? "text-primary-600" : "text-foreground-300 group-hover:translate-x-1"
                    }`}
                  ></i>
                </button>
              ))}
              <a
                href="#products"
                className="btn-primary mt-6 w-fit h-11 px-5 text-xs"
              >
                View All Products <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Mobile: View All Products button below horizontal pills */}
            <a
              href="#products"
              className="btn-primary mt-4 w-fit h-11 px-5 text-xs lg:hidden"
            >
              View All Products <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}